import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import {
    formatDateToDayMonth,
} from '../../../../lib/utils/utils';
import { getPreviousSixMonths, parseAPIDate, toLocalISODateYYYYMMDD } from '../../../../lib/utils/graph.utils';

function SalesOverviewTooltip({ active, payload, label }) {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#121212] text-[#FFFFFF] rounded-lg p-2">
                <p className="font-semibold mb-1">{label}</p>

                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div
                                className="size-3 rounded"
                                style={{ backgroundColor: entry.color }}
                            ></div>
                            <span className="font-medium">
                                {entry.name === 'totalSales' ? 'Sales' : 'Orders'}
                            </span>
                        </div>

                        <span className="text-[#22C55E] font-semibold">{entry.name === 'totalSales' && '₹'}{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
}

function SalesOverview() {
    const { salesInsights } = useSelector((state) => state.reportAnalysis.salesInsights);
    const reportRange = useSelector((state) => state.reportAnalysis.reportRange);

    const [overviewData, setOverviewData] = useState([]);
    const [upperLimit, setUpperLimit] = useState(0);

    useEffect(() => {
        if (Object.keys(salesInsights).length === 0) return;

        if (reportRange === 'lastWeek') {
            if (salesInsights.salesOverview?.salesLastWeek?.length > 0) {
                // Use the same padding logic as the first component
                let src = salesInsights.salesOverview.salesLastWeek.slice(-7);
                if (src.length > 0 && src.length < 7) {
                    const needed = 7 - src.length;
                    const firstDate = parseAPIDate(src[0].date);
                    const pad = [];
                    for (let i = needed; i >= 1; i--) {
                        const d = new Date(firstDate);
                        d.setDate(d.getDate() - i);
                        pad.push({
                            date: toLocalISODateYYYYMMDD(d),
                            totalMoneyValue: 0,
                            totalOrders: 0,
                            _dummy: true,
                        });
                    }
                    src = [...pad, ...src];
                }

                const result = src.map(d => {
                    const dt = parseAPIDate(d.date);
                    const dayOfWeek = dt.toLocaleDateString(undefined, { weekday: 'short' });
                    return {
                        dayOfWeek: dayOfWeek,
                        label: `${dayOfWeek} ${formatDateToDayMonth(d.date)}`,
                        totalSales: Number(d.totalMoneyValue) || 0,
                        totalOrders: Number(d.totalOrders) || 0,
                    };
                });

                setOverviewData(result);
            } else {
                setOverviewData([]);
            }
        } else if (reportRange === 'lastMonth') {
            if (salesInsights.salesOverview?.salesLastMonth?.length > 0) {
                // Use the same padding logic as the first component
                let src = salesInsights.salesOverview.salesLastMonth.slice(-30);
                if (src.length > 0 && src.length < 30) {
                    const needed = 30 - src.length;
                    const firstDate = parseAPIDate(src[0].date);
                    const pad = [];
                    for (let i = needed; i >= 1; i--) {
                        const d = new Date(firstDate);
                        d.setDate(d.getDate() - i);
                        pad.push({
                            date: toLocalISODateYYYYMMDD(d),
                            totalMoneyValue: 0,
                            totalOrders: 0,
                            _dummy: true,
                        });
                    }
                    src = [...pad, ...src];
                }

                const result = src.map(d => ({
                    dayOfMonth: formatDateToDayMonth(d.date),
                    label: formatDateToDayMonth(d.date),
                    totalSales: Number(d.totalMoneyValue) || 0,
                    totalOrders: Number(d.totalOrders) || 0,
                }));

                setOverviewData(result);
            } else {
                setOverviewData([]);
            }
        } else if (reportRange === 'allTime') {
            // Implement the missing allTime logic
            if (!Array.isArray(salesInsights.salesOverview?.salesAllTime) || salesInsights.salesOverview.salesAllTime.length === 0) {
                setOverviewData([]);
                return;
            }
            
            const monthsArray = getPreviousSixMonths();
            const result = monthsArray.map(monthObj => {
                const salesData = salesInsights.salesOverview.salesAllTime.find(s => s.month === monthObj.monthKey);
                return {
                    monthYear: monthObj.monthYear,
                    label: monthObj.monthYear,
                    totalSales: salesData ? Number(salesData.totalMoneyValue) : 0,
                    totalOrders: salesData ? Number(salesData.totalOrders) : 0,
                };
            });
            setOverviewData(result);
        }
    }, [salesInsights, reportRange]);

    useEffect(() => {
        if (overviewData.length === 0) return;
        
        // Fix upper limit calculation to consider both sales and orders
        const maxY = Math.max(
            ...overviewData.map(i => Math.max(i.totalSales || 0, i.totalOrders || 0))
        );
        const base = 500;
        const roundedLimit = Math.max(base, Math.ceil((maxY || 1) / base) * base);
        setUpperLimit(roundedLimit);
    }, [overviewData]);

    // Fix the xKey logic to handle all cases
    const xKey = 
        reportRange === 'lastWeek' 
            ? 'dayOfWeek' 
            : reportRange === 'lastMonth' 
            ? 'dayOfMonth' 
            : 'monthYear'; // Add monthYear for allTime

    return (
        <div className="border rounded-lg border-[#EAEAEA] w-full min-h-[50vh] p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h1 className="font-semibold text-base">Sales Overview</h1>

                <div className="flex items-center gap-3">
                    {/* Legend */}
                    {overviewData.length > 0 && (
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <div
                                    className="size-3 rounded"
                                    style={{ backgroundColor: '#1055EB' }}
                                />
                                <p className="font-semibold text-[12px] leading-6 text-[#000000]">
                                    Sales
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <div
                                    className="size-3 rounded"
                                    style={{ backgroundColor: '#22C55E' }}
                                />
                                <p className="font-semibold text-[12px] leading-6 text-[#000000]">
                                    Orders
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {overviewData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={overviewData}
                        margin={{
                            top: 10,
                            right: 20,
                            bottom: 10,
                            left: 10,
                        }}
                    >
                        <XAxis
                            dataKey={xKey}
                            tickLine={false}
                            tickMargin={15}
                            type="category"
                            interval={reportRange === 'lastWeek' ? 0 : reportRange === 'lastMonth' ? 3 : 0}
                            padding={{ right: 20 }}
                            allowDuplicatedCategory={true}
                        />

                        <YAxis
                            tickCount={6}
                            tickLine={false}
                            tickMargin={15}
                            type="number"
                            domain={[0, upperLimit]}
                            width={75}
                        />

                        <Tooltip 
                            content={<SalesOverviewTooltip />}
                            labelFormatter={(label, payload) => {
                                const p = payload && payload[0] && payload[0].payload;
                                return p?.label ?? label;
                            }}
                        />

                        <Line
                            dot={false}
                            isAnimationActive={false}
                            type="monotone"
                            dataKey="totalSales"
                            stroke="#1055EB"
                            strokeWidth={2}
                        />
                        <Line
                            dot={false}
                            isAnimationActive={false}
                            type="monotone"
                            dataKey="totalOrders"
                            stroke="#22C55E"
                            strokeWidth={2}
                        />
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <div className="m-auto">
                    <h1 className="font-normal italic text-base text-[#666666]">
                        No data available
                    </h1>
                </div>
            )}
        </div>
    );
}

export default SalesOverview;